import axios from "axios";
import { cacheGet, cachePut, isNetworkError } from "@/lib/offlineCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Token fallback (cuando el navegador bloquea las cookies de sesión) ----
const TOKEN_KEY = "ef_access_token";
const REFRESH_KEY = "ef_refresh_token";

export function setStoredTokens(access, refresh) {
  try {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch { /* almacenamiento no disponible */ }
}
export function clearStoredTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch { /* noop */ }
}
const getStoredToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const getStoredRefresh = () => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } };
export { getStoredToken };

// La cookie httpOnly sigue siendo la vía principal; el header solo actúa
// como respaldo si el navegador no envía la cookie.
api.interceptors.request.use((cfg) => {
  const t = getStoredToken();
  if (t && !cfg.headers?.Authorization) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

/**
 * Global event bus for the offline banner.
 * Emitted whenever a cached response is returned as a network fallback.
 */
function emitOffline(savedAt) {
  window.dispatchEvent(
    new CustomEvent("offline-fallback", { detail: { savedAt } }),
  );
}
function emitOnline() {
  window.dispatchEvent(new CustomEvent("online-restored"));
}

// Store successful GETs; emit "online" on any successful request.
api.interceptors.response.use(
  (res) => {
    if (res.config?.method?.toLowerCase() === "get") {
      const url = api.getUri(res.config);
      cachePut(url, res.data);
    }
    emitOnline();
    return res;
  },
  async (err) => {
    const cfg = err?.config;
    if (cfg && cfg.method?.toLowerCase() === "get" && isNetworkError(err)) {
      const url = api.getUri(cfg);
      const cached = await cacheGet(url);
      if (cached) {
        emitOffline(cached.savedAt);
        // Fake an axios response so downstream code doesn't need special-casing.
        return {
          data: cached.data,
          status: 200,
          statusText: "OK (offline cache)",
          headers: {},
          config: cfg,
          __offline: true,
          __savedAt: cached.savedAt,
        };
      }
    }
    return Promise.reject(err);
  },
);

// Silent session renewal: on 401, try /auth/refresh once and retry the request.
const NO_REFRESH = ["/auth/login", "/auth/logout", "/auth/refresh", "/auth/register"];
let refreshPromise = null;
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err?.config;
    const url = cfg?.url || "";
    if (
      err?.response?.status === 401 &&
      cfg && !cfg._retry &&
      !NO_REFRESH.some((p) => url.includes(p))
    ) {
      cfg._retry = true;
      try {
        refreshPromise =
          refreshPromise ||
          api.post("/auth/refresh", { refresh_token: getStoredRefresh() })
            .then((r) => { setStoredTokens(r.data?.access_token, r.data?.refresh_token); return r; })
            .finally(() => { refreshPromise = null; });
        await refreshPromise;
        return api(cfg);
      } catch {
        // Refresh failed → session really expired; fall through to original 401.
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) {
    if (isNetworkError(err))
      return "Sin conexión. Guarda cuando vuelvas a tener red.";
    return err?.message || "Error desconocido";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
